/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useMemo} from 'react';
import {useQuery} from 'react-query';
import {error} from '@ion/core/src/logger/logger';
import {loadComplianceDashboard} from '@ion/core/src/services/Connect';
import {GenericObject} from '@ion/core/src/types/entities';
import {LoadComplianceDashboardParams} from '@ion/core/src/types/services';
import {createKey, QueryKey} from '../config/QueryKeys';
import {createQueryResult, DurationResult, getStaleTime} from '../utils/helpers';

export interface UseComplianceDashboardQueryProps extends LoadComplianceDashboardParams {
  staleTime?: number | DurationResult;
  enabled?: boolean;
}

export interface UseComplianceDashboardQueryResult {
  isFetching: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  data: GenericObject;
  totalCount: number;
}

/**
 * @useComplianceDashboardQuery
 *
 * This hook provides pagination functionality for loading a Compliance Dasnboard.
 *
 * ```typescript
 * import {useComplianceDashboardQuery} from '@ion/query/src/hooks/useComplianceDashboardQuery';
 *
 * const {
 *   isFetchingNextPage,
 *   fetchNextPage,
 *   hasNextPage,
 *   data
 * } = useComplianceDashboardQuery({
 *   accountId : 5099,
 *   sortOn    : ['+deviceId']
 * });
 * ```
 *
 * @param accountId   - The account ID for which assets are retrieved.
 * @param pageSize    - Number of results per page.
 * @param staleTime   - Cache duration in milliseconds.
 * @param enabled     - Whether the query should run automatically.
 *
 * @returns An object containing query state and methods to fetch more data.
 */
export const useComplianceDashboardQuery = ({
  accountId,
  esn,
  pageSize = 20,
  page = 1,
  staleTime = getStaleTime(1, false, false),
  enabled = true,
  ...searchFields
}: UseComplianceDashboardQueryProps): UseComplianceDashboardQueryResult => {
  const key = useMemo(function() {
    return createKey(
      QueryKey.COMPLIANCE_DASHBOARD,
      accountId,
      esn,
      pageSize,
      page
    );
  }, [accountId, pageSize, page]);

  const query = useQuery(key, async() => {
    try {
      const response = await loadComplianceDashboard({
        pageId : page,
        accountId,
        pageSize,
        esn,
        ...searchFields
      });
      return response || {carb: [], totalCount: 0};
    } catch (err) {
      error('Error fetching Dashboard:', err);
      return {carb: [], totalCount: 0};
    }
  },
  {
    staleTime : staleTime as number,
    enabled
  }
  );

  return createQueryResult(query, {
    data       : query.data || [],
    totalCount : query.data?.totalCount ?? 0
  }) as UseComplianceDashboardQueryResult;
};

export default useComplianceDashboardQuery;
